import { immerable } from "immer";
import { DayPart } from "../lib/reservation.service";
import { Customer } from "./customer";
import { Menu, OnlineReservationCategory } from "./login-response.dto";
import { ReservationTag } from "./reservation-tag";
import { Source } from "./source";
import { TableImageType, TableRotation } from "./tables-plan";

export class Reservation {
    [immerable] = true;

    isConflict?: boolean;
    modifiedBy?: string;
    typeOfModification?: any;
    timePerformed?: string;
    reservationDetails: ReservationDetails = new ReservationDetails();
    customerDetails: Customer = new Customer();
    sourceDetails: Source | null = null;
    hasSource?: boolean;
    sourceCategory?: string | null;
    sourceType?: string | null;
    sourceTypeOfVisit?: string | null;
}

export class ReservationDetails {
    [immerable] = true;

    reservationId: string | undefined | null = null;
    tmpId: string | undefined | null = null;
    arrivalTime: string = '';
    returnTime: string = '';
    duration?: number;
    reservationsStatus: ReservationStatus = 'NotConfirmed';
    covers: number = 0;
    allocatedTables: string = '';
    discount: number | string = 0;
    notes: string = '';
    bill: number = 0;
    purpose: ReservationPurpose = 'Food';
    allergy?: string = '';
    smokingArea?: boolean;
    hasCake?: boolean;
    reservationType?: string;
    createdBy?: string;
    creationTime?: number;
    pocket?: boolean;
    channel?: string;
    confirmedBy?: string | null;
    walkIn?: boolean;
    lateBooking?: boolean;
    selectedMenu?: string | null;
    fixMenu?: boolean;
    onlineCategory?: OnlineReservationCategory | null;
    restaurantId?: string;
    reservationTags?: ReservationTag[];
    tokenization?: boolean;
    deposit?: boolean;
    expiredArrivalTime?: boolean;
    expiredReturnTime?: boolean;
    preorder?: boolean;
    extraInfo?: string;
    roomNumber?: string | null;
    roomBookingId?: string | null;
    metadata: string | null = null;
}

export class AuditTrail {
    customerDetails: Customer = new Customer();
    diffs: AuditTrailDiff[] = [];
    modifiedBy: string = '';
    reservationDetails: ReservationDetails = new ReservationDetails();
    sourceDetails: Source = new Source();
    typeOfModification: 'CREATED' | 'EDITED' | 'CANCELLED' | 'NOTSHOWN' = 'CREATED';
    timeperformed?: string;

    displayedDiffs: string = '';
}

export class AuditTrailDiff {
    resField: string = '';
    oldValue: string = '';
    newValue: string = '';
}

export type ReservationStatus =
    'Waiting' |
    'Pending' |
    'NotConfirmed' |
    'Confirmed' |
    'LateBooking' |
    'CheckedIn(Partial)' |
    'CheckedIn' |
    'WalkIn' |
    'Dessert' |
    'Payment' |
    'Prepare' |
    'CheckedOut' |
    'NotShown' |
    'Cancelled' |
    'Rejected';

export type ReservationPurpose =
    'All' |
    'Food' |
    'Drink';


export type CustomerType =
    'S' | // Simple
    'R' | // Regular
    'VIP' | // VIP
    'R&VIP' | // Regular VIP
    'SVIP' | // Super VIP
    'BL'; // Black List

export class Shift {
    shiftId?: string = '';
    fromHour: number = 0;
    fromMin: number = 0;
    toHour: number = 0;
    toMin: number = 0;
    isActive?: boolean;
    settingsId?: string;
    title?: string;
    type?: ShiftType;
    isFullDay?: boolean;

    parts?: DayPart[];
    totalCovers?: number;
    totalRes?: number;
    dayParts?: DayPart[];
    timeperformed?: number;
}

export enum ShiftType {
    Morning = 0,
    Afternoon = 1,
    Night = 2,
    Midnight = 3,
    AllDay = 4
}

export class TablesSection {
    sectionId: string = '';
    sectionName: string = '';
    sectionCapacity: number = 0;
    sectionOrder: number = 0;
    excludeOnline?: boolean;
    sectionTables: Table[] = [];
}

export interface Table {
    tableId: string;
    sectionId: string;
    workingDateId: string;
    tableName: string;
    minCapacity: number;
    maxCapacity: number;
    tableOrder: number;
    x: number;
    y: number;
    rotation: TableRotation;
    imgType: TableImageType;
    available?: boolean;
    beforeAvailable?: boolean;
    beforeArrival?: boolean;
    beforeReturn?: string;
    afterArrival?: boolean;
    afterReturn?: string;
    afterAvailable?: boolean;
    proposed?: boolean;
    version?: number;
    excludeOnline?: boolean;
    excludeOnlineThirdParty?: boolean;
    joinTablesIds?: string;
    block?: boolean;
    onlineCategory?: OnlineReservationCategory | null;
    timeperformed?: number;
}

export enum SortType {
    Status,
    Surname,
    Cover,
    Time,
    Table,
    CreatedAt,
    Category,
    // ExtraInfo,
    // Source
}

export type ReservationListFilters = {
    groupByStatus: boolean;
    withDietary: boolean;
    withType: boolean;
    withResvTags: boolean;
    withNotes: boolean;
    withDiscount: boolean;
    withBill: boolean;
    withCake: boolean;
    withSource: boolean;
    withFood: boolean;
    withDrink: boolean;
    withTokenization: boolean;
    //withDeposit: boolean;
    reservationCategories: (string | undefined)[];
}

export interface ManualPaymentActionResponse {
    Success: boolean;
    Error: string;
    ErrorDetails: string;
}

export type TableSlot = {
    slotNumber: 1 | 2 | 3,
    reservation: Reservation | null,
}

export interface EOrder {
    id: string;
    externalId: string;
    uiId: string;
    status: string;
    type: string;
    creationTime: number;
    day: string;
    time: string;
    lastModified: number;
    expectedBy: string;
    payment: string;
    notes: string;
    rejectionReason: string;
    languageCode: string;
    totalCost: number;
    deliveryFee: number;
    address: string;
    tableName: string;
    roomName: string;
    metaData: string;
    couponCode: string;
    orderLines: EOrderLine[];
    menuId: string;
}

export interface EOrderLine {
    id: string;
    externalId: string;
    quantity: number;
    notes: string;
    totalPrice: number;
    productId: string;
    productJson: string;
    orderId: string
}

export type DoubleBookingCheckFields =
    | 'MAIL'
    | 'PHONE'
    | 'ROOM_NUMBER'
    | 'BOOKING_ID'
    | 'EXTERNAL_CUSTOMER_ID'
    | 'NAME'
    | 'SURNAME';

export interface DoubleBookingResponse {
    conflict: boolean;
    matchedFields: DoubleBookingCheckFields[];
    success: boolean;
    error?: string | null;
}

export interface DoubleBookingRequest {
    restaurantId?: string;
    surname?: string;
    name?: string;
    bookingId?: string;
    roomNumber?: string;
    mail?: string;
    externalCustomerId?: string;
    phone?: string;
    arrival?: string;
}


export interface DoubleBookingConflictReservation {
    id: string;
    status: string;
    arrivalAt: string;
    covers: number;
    surname: string;
    name: string;
    restaurantId: string;
    note: string;
    roomNumber: string;
    roomBookingID: string;
    restaurantTitle: string;
    categoryId: string;
    categoryNameLocal: string;
    categoryNameEnglish: string;
    matchedRules: DoubleBookingCheckFields[][];
}

export interface DoubleBookingResponseV2 extends DoubleBookingResponse {
    conflictReservations: DoubleBookingConflictReservation[];
}


export interface PreauthStatusResponse {
  Success: boolean;
  Exists: boolean;
  Actionable: boolean;
  CanReserve: boolean;              // NEW
  ReserveBlockedReason?: string | null; // NEW
  CaptureBefore?: number | null;
  PaymentIntentId: string;
  Status: string;
  Amount: number;   // cents
  Currency: string;
  Created: number;  // unix seconds
  Error: string;
  ErrorDetails: string;
}

export interface PreauthListItem {
  PaymentIntentId?: string;
  Status?: string;
  Amount?: number; // cents
  Currency?: string;
  Created?: number; // unix seconds
  CaptureBefore?: number | null; // unix seconds
  AmountRefunded?: number; // cents
  Refunded?: boolean;
}

export interface PreauthListResponse {
  Success: boolean;
  Items?: PreauthListItem[];
  Error?: string;
  ErrorDetails?: string;
}

export interface PendingPreauthAction  {
  kind: 'capture' | 'release' | 'refund';
  paymentIntentId: string;
}

export interface ManualReserveResponse {
  Success: boolean;
  PaymentIntentId?: string;
  Status?: string;
  Amount?: number;       
  Currency?: string;      
  Created?: number;       
  CaptureBefore?: number; 
  RequiresAction?: boolean;

  Error?: string;
  ErrorDetails?: string;
}

export interface PaymentLinkExpirationGetResponse {
  Status: boolean;
  ErrorCode?: string | null;
  ErrorDescription?: string | null;
  reservationId: string;
  paymentLinkExpiresAt: string | null; // server returns datetime string (ISO or "yyyy-MM-dd HH:mm:ss")
}

export interface PaymentLinkExpirationUpdateRequest {
  reservationId: string;
  paymentLinkExpiresAt: string | null; // send ISO string recommended
}

export interface SlotSummary  {
 covers: number;
 reservations: number;
}

export interface ReservationsExportFieldSettings {
  status: boolean;
  purpose: boolean;
  arrivalDate: boolean;
  arrivalTime: boolean;
  fullname: boolean;
  covers: boolean;
  type: boolean;
  telephone: boolean;
  allergies: boolean;
  reservationTags: boolean;
  customerTags: boolean;
  discount: boolean;
  cake: boolean;
  fixMenu: boolean;
  smokingArea: boolean;
  email: boolean;
  category: boolean;
  reservationId: boolean;
  tables: boolean;
  duration: boolean;
  returnTime: boolean;
  createdBy: boolean;
  confirmedBy: boolean;
  notes: boolean;
  source: boolean;
  sourceId: boolean;
  customerId: boolean;
  bill: boolean;
  restaurantName: boolean;
  creationTime: boolean;
  timePerformed: boolean;
  metadata: boolean;
  delimiter: string;
}

export interface ReservationsCsvExportParams {
  reservations: Reservation[];
  settings: ReservationsExportFieldSettings;
  selectedShift?: Shift | null;
  selectedShiftTitle?: string | null;
  selectedDate?: Date | string | null;
  restaurantTitle?: string | null;
  fileNameOverride?: string | null;
}

export interface DelimiterOption {
  value: string;
  display: string;
}